// ============================================================================
// ENUMS
// ============================================================================

export enum AdminPlanCategory {
  B2C = 'B2C',
  B2B = 'B2B',
  SPECIAL = 'SPECIAL',
}

export enum AdminPlanStatus {
  DRAFT = 'DRAFT',
  REVIEW = 'REVIEW',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  DEPRECATED = 'DEPRECATED',
  ARCHIVED = 'ARCHIVED',
}

export enum AdminSubscriptionStatus {
  TRIAL = 'TRIAL',
  ACTIVE = 'ACTIVE',
  PAST_DUE = 'PAST_DUE',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
  SUSPENDED = 'SUSPENDED',
}

export enum AdminBillingPeriod {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  LIFETIME = 'LIFETIME',
}

export enum FeatureValueType {
  BOOLEAN = 'BOOLEAN',
  NUMBER = 'NUMBER',
  TIER = 'TIER',
}

// ============================================================================
// INTERFACES
// ============================================================================

export interface AdminCurrency {
  id: string;
  code: string;
  name: string;
  symbol: string;
  decimals: number;
  exchangeRate: number;
  manualRate?: number | null;
  isActive: boolean;
  isDefault: boolean;
  lastRateUpdate?: Date | null;
}

export interface AdminFeatureDefinition {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  category: string;
  valueType: FeatureValueType;
  unit?: string | null;
  sortOrder: number;
  isPublic: boolean;
  icon?: string | null;
}

export interface AdminLimitDefinition {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  category: string;
  unit?: string | null;
  defaultValue: number;
  sortOrder: number;
  isPublic: boolean;
  icon?: string | null;
}

export interface AdminPlanFeature {
  id: string;
  isIncluded: boolean;
  value?: string | null;
  tierLevel?: number | null;
  tooltip?: string | null;
  feature: AdminFeatureDefinition;
}

export interface AdminPlanLimit {
  id: string;
  value: number;
  isUnlimited: boolean;
  resetPeriod?: string | null;
  tooltip?: string | null;
  limit: AdminLimitDefinition;
}

export interface AdminPlanRewards {
  id: string;
  multiplier: number;
  cashbackPercent: number;
  signupBonus: number;
  referralBonus: number;
  referralPercent?: number | null;
  monthlyBonus: number;
  yearlyBonus?: number | null;
  exclusiveRewards: string[];
  priorityAccess: boolean;
}

export interface AdminPlanCurrencyPrice {
  currencyCode: string;
  currencySymbol: string;
  monthlyPrice: number;
  yearlyPrice?: number | null;
}

export interface AdminPlan {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  category: AdminPlanCategory;
  status: AdminPlanStatus;
  sortOrder: number;
  isPopular: boolean;
  monthlyPrice: number;
  yearlyPrice?: number | null;
  yearlyDiscount?: number | null;
  trialDays: number;
  colorPrimary?: string | null;
  colorSecondary?: string | null;
  icon?: string | null;
  badges: string[];
  highlights: string[];
  maxUsers?: number | null;
  migrationToId?: string | null;
  deprecationDate?: Date | null;
  createdAt: Date;
  updatedAt: Date;
  features?: AdminPlanFeature[];
  limits?: AdminPlanLimit[];
  rewards?: AdminPlanRewards | null;
  currencies?: AdminPlanCurrencyPrice[];
}

export interface SubscriptionUser {
  id: string;
  email?: string | null;
}

export interface SubscriptionEnterprise {
  id: string;
  name: string;
}

export interface SubscriptionPlan {
  id: string;
  code: string;
  name: string;
  monthlyPrice?: number;
  yearlyPrice?: number | null;
  colorPrimary?: string | null;
}

export interface SubscriptionPayment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  providerTxnId?: string | null;
  paidAt?: Date | null;
  createdAt: Date;
}

export interface SubscriptionHistory {
  id: string;
  action: string;
  fromStatus?: string | null;
  toStatus?: string | null;
  reason?: string | null;
  performedBy?: string | null;
  createdAt: Date;
}

export interface AdminUserSubscription {
  id: string;
  status: AdminSubscriptionStatus;
  billingPeriod: AdminBillingPeriod;
  startDate: Date;
  endDate: Date;
  trialEndDate?: Date | null;
  nextBillingDate?: Date | null;
  priceAmount: number;
  priceCurrency: string;
  autoRenew: boolean;
  discountPercent?: number | null;
  discountCode?: string | null;
  paymentMethod?: string | null;
  cancelledAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
  userId?: string | null;
  enterpriseId?: string | null;
  plan?: SubscriptionPlan | null;
  user?: SubscriptionUser | null;
  enterprise?: SubscriptionEnterprise | null;
  payments?: SubscriptionPayment[];
  history?: SubscriptionHistory[];
}

export interface PlanSubscriptionCount {
  planCode: string;
  count: number;
}

export interface SubscriptionStats {
  total: number;
  byStatus: Record<string, number>;
  byPlan: PlanSubscriptionCount[];
  mrr: number;
  arr: number;
}

export interface SubscriptionConnection {
  items: AdminUserSubscription[];
  total: number;
  page: number;
  pageSize: number;
}

// ============================================================================
// HELPERS
// ============================================================================

export function formatPrice(amount: number, currency: string = 'XOF'): string {
  if (currency === 'XOF' || currency === 'FCFA') {
    return `${amount.toLocaleString('fr-FR')} FCFA`;
  }
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDateTime(date: Date | string): string {
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const PLAN_STATUS_LABELS: Record<AdminPlanStatus, string> = {
  [AdminPlanStatus.DRAFT]: 'Brouillon',
  [AdminPlanStatus.REVIEW]: 'En révision',
  [AdminPlanStatus.ACTIVE]: 'Actif',
  [AdminPlanStatus.PAUSED]: 'En pause',
  [AdminPlanStatus.DEPRECATED]: 'Obsolète',
  [AdminPlanStatus.ARCHIVED]: 'Archivé',
};

export const PLAN_STATUS_COLORS: Record<AdminPlanStatus, string> = {
  [AdminPlanStatus.DRAFT]: '#9CA3AF',
  [AdminPlanStatus.REVIEW]: '#F59E0B',
  [AdminPlanStatus.ACTIVE]: '#10B981',
  [AdminPlanStatus.PAUSED]: '#6366F1',
  [AdminPlanStatus.DEPRECATED]: '#F97316',
  [AdminPlanStatus.ARCHIVED]: '#6B7280',
};

export const SUBSCRIPTION_STATUS_LABELS: Record<AdminSubscriptionStatus, string> = {
  [AdminSubscriptionStatus.TRIAL]: 'Essai',
  [AdminSubscriptionStatus.ACTIVE]: 'Actif',
  [AdminSubscriptionStatus.PAST_DUE]: 'Impayé',
  [AdminSubscriptionStatus.CANCELLED]: 'Annulé',
  [AdminSubscriptionStatus.EXPIRED]: 'Expiré',
  [AdminSubscriptionStatus.SUSPENDED]: 'Suspendu',
};

export const SUBSCRIPTION_STATUS_COLORS: Record<AdminSubscriptionStatus, string> = {
  [AdminSubscriptionStatus.TRIAL]: '#8B5CF6',
  [AdminSubscriptionStatus.ACTIVE]: '#10B981',
  [AdminSubscriptionStatus.PAST_DUE]: '#F59E0B',
  [AdminSubscriptionStatus.CANCELLED]: '#EF4444',
  [AdminSubscriptionStatus.EXPIRED]: '#6B7280',
  [AdminSubscriptionStatus.SUSPENDED]: '#DC2626',
};

export const BILLING_PERIOD_LABELS: Record<AdminBillingPeriod, string> = {
  [AdminBillingPeriod.MONTHLY]: 'Mensuel',
  [AdminBillingPeriod.YEARLY]: 'Annuel',
  [AdminBillingPeriod.LIFETIME]: 'À vie',
};

export const CATEGORY_LABELS: Record<AdminPlanCategory, string> = {
  [AdminPlanCategory.B2C]: 'B2C',
  [AdminPlanCategory.B2B]: 'B2B / Enterprise',
  [AdminPlanCategory.SPECIAL]: 'Spécial',
};

export const FEATURE_CATEGORY_LABELS: Record<string, string> = {
  cards: 'Cartes Digitales',
  events: 'Événements',
  crm: 'CRM',
  commerce: 'Commerce',
  support: 'Support',
  technical: 'Technique',
};

export const LIMIT_CATEGORY_LABELS: Record<string, string> = {
  usage: 'Utilisation',
  storage: 'Stockage',
  team: 'Équipe',
  api: 'API',
  marketing: 'Marketing',
};
